import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error';
}

interface ToastProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const DURATION = 3000;

  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, DURATION);

    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: "100%" }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      className={cn(
        "rounded-lg p-4 mb-2 shadow-lg relative overflow-hidden transform-gpu",
        toast.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
      )}
    >
      <div className="flex justify-between items-center">
        <p>{toast.message}</p>
        <button
          onClick={() => onDismiss(toast.id)}
          className="ml-4 p-1 hover:bg-black/5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <motion.div
        initial={{ scaleX: 1 }}
        animate={{ scaleX: 0 }}
        transition={{ duration: DURATION / 1000, ease: "linear" }}
        className={cn(
          "absolute bottom-0 left-0 right-0 h-1 origin-left",
          toast.type === 'success' ? 'bg-green-200' : 'bg-red-200'
        )}
      />
    </motion.div>
  );
};

export default Toast;